/* eslint-disable max-lines, header/header */
// TODO Refaktorovat
// - Nabízí se vyčlenění metod, které operují nad FileInfo (např. listFile)
//   a ne nad Asset.
// - Samostatný generátor identifikátorů.
// - Rozdělit na dvě třídy pro stahování souborů a pro stahování archivů.
//   Na začátku se dle extract rozhodnout, který použít.
//   Případně extract vůbec do této třídy nepředávat
//   a rozhodnutí provést ve vyšší třídě např. ViewerType#getDownloader.
//   Aktuální řešení s `if (extract) {...}` není ideální.

/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import omitBy from 'lodash/omitBy';
import uniq from 'lodash/uniq';
import objectHash from 'object-hash';

import ErrorLevel from '../../error/error-level';
import ErrorName from '../../error/error-name';

// TODO #471 Doc: Upravit poznámky, že se checksum používá jen pro legacy assety.
// TODO Přejmenovat na AssetManager? AssetCache? AssetCacher? FileTransferCache?
// - Ještě asi nastudovat nějaký oficiální algoritmus pro cache, jestli se na něco nezapomnělo.
// - https://developer.mozilla.org/en-US/docs/Web/API/Cache
// TODO Občas může dojít ke zbytečnému stažení souboru při vypnuté cache.
// - Podmínkou je, aby se soubor změnil na verzi, ve které už byl.
//   Např. z jedné URL se stáhne soubor 1, potom soubor 2 a nakonec znovu 1.
//   V tom okamžiku jsou v cache oba soubory, ale přesto se soubor 1 znovu stáhne.
//   Důvodem je, že v If-None-Match se posílá jen poslední etag.
//   Řešením by bylo posílat v něm všechny známé ETag a pak z výsledku převzít ETag, který se má použít.
//   V hlavičcce If-Modified-Since by se pravděpodobně mělo posílat poslední datum Last-Modified.
// - Zatím toto není ošetřeno, protože se takto cache většinou nepoužívá.
//   Důsledky také nejsou kritické, jen se provede zbytečné stažení souboru,
//   který se následně smaže, jinak vše funguje.
// TODO V ideálním případě by asi měl AssetDownloader implementovat FileTransferManager
// - Nebo alespoň část AssetDownloader po jeho refaktorování.
// - V podstatě by se k jeho funkcionalitě přidávalo cachování,
//   jinak by se API shodovalo.
// - Ale je zde řada problémů, např. co s extract?
//   Všechny cache fungují na základě cachování souborů, nikoliv složek.
//   Také je zbytečné cachovat stažený archiv a vedle něj mít rozbalenou složku.
// - Dále se cache většinou zakládají nejen na obsahu URL,
//   ale také na obsahu hlaviček a typu HTTP metody.
// - Dále se z cache vrací celý objekt Response, ne jen cesta k souboru.
/**
 * Stahovač a organizér záznamů v souborovém systému
 *
 * Využívá obecné rozhraní ke stahování souborů a jejich organizaci na disku.
 * Funguje jako cache pro stahované soubory a opakované požadavky na stažení
 * ze stejné URL může vracet z této cache bez odeslání požadavku na server.
 * Spravuje metadata o uložených souborech.
 * Provádí operace nad soubory podle nastavení jako je `cache`
 * (kontrola aktuální verze) nebo `extract`
 * (rozbalení souborů s archivy do složky).
 */
class AssetDownloader {
  /**
   * @param {FileSystemPath} basePath Cesta ke složce, kde se ukládají záznamy.
   * @param {FileArchiveManager} fileArchiveManager Správce archivů.
   * @param {FileSystemPathFactory} fileSystemPathFactory Továrna na cesty v souborovém systému.
   * @param {FileSystemManager} fileSystemManager Správce souborového systému.
   * @param {FileTransferManager} fileTransferManager Správce stahování.
   * @param {Logger} logger
   * @param {PathBrowserify} pathBrowserify
   * @param {PromiseUtil} promiseUtil
   * @param {Store} store
   */
  constructor(
    basePath,
    fileArchiveManager,
    fileSystemPathFactory,
    fileSystemManager,
    fileTransferManager,
    logger,
    pathBrowserify,
    promiseUtil,
    store
  ) {
    this.basePath = basePath;
    this.fileArchiveManager = fileArchiveManager;
    this.fileSystemManager = fileSystemManager;
    this.fileSystemPathFactory = fileSystemPathFactory;
    this.fileTransferManager = fileTransferManager;
    this.logger = logger;
    this.pathBrowserify = pathBrowserify;
    this.promiseUtil = promiseUtil;
    this.store = store;
    /**
     * Přípona souboru, která označuje dočasný soubor.
     * Používá se při stahování, aby šlo kompletně nedostahované soubory
     * identifikovat a smazat.
     * Pří úspěšném stažení se soubor přesune do finálního umístění
     * bez této přípony.
     * @private
     * @type {string}
     */
    this.temporaryExtension = 'tmp';
  }

  /**
   * Vytvoří nový záznam, který ještě není cachovaný.
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {URL} url URL adresa, ze které se má stáhnout soubor.
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum]
   * @private
   * @returns {Promise.<Asset>}
   */
  create(extract, url, { checksum } = {}) {
    return new Promise((resolve) => {
      const temporaryDirectoryPath = this.parseTemporaryDirectoryPath(url);
      const temporaryFilePath = this.parseTemporaryFilePath(url);
      // Zjištění názvu souboru, validace a parsování jeho přípony.
      // Určuje se předem, aby se zbytečně nestahovalo,
      // pokud by přípona nebyla validní.
      const extension = this.parseExtension(url);
      return resolve(
        // Vytvoří se pracovní složka.
        this.fileSystemManager
          .createDirectory(this.basePath)
          // Pokud již pracovní složka existuje, nevadí to.
          // TODO Přidat do createDirectory příznak pro tolerování existující složky.
          // - Nyní nelze atomickým dotazem rozeznat, jestli je na cestě složka nebo soubor.
          .catch((error) => {
            if (error.name === ErrorName.PathExistsError) {
              return;
            }
            throw error;
          })
          .then(() =>
            this.fileTransferManager.download(temporaryFilePath, url.toString())
          )
          .then(
            ({ file, response }) =>
              this.createDownloaded(
                extension,
                extract,
                response,
                temporaryDirectoryPath,
                file,
                url,
                { checksum }
              )
                // Ošetření selhání v operacích po stažení souboru.
                .catch((error) => {
                  // Pokud soubor nebyl stažen, nic se nedělá.
                  if (isNil(file)) {
                    throw error;
                  }
                  // Pokud byl soubor stažen, smaže se.
                  return file.remove().then(() => {
                    throw error;
                  });
                }),
            (error) => {
              if (error.name === ErrorName.NetworkError) {
                error.level = ErrorLevel.Warning;
              }
              throw error;
            }
          )
      );
    });
  }

  /**
   * Dokončí vytvoření nového záznamu po jeho stažení.
   * @param {string} extension
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {Response} response
   * @param {FileSystemPath} temporaryDirectoryPath
   * @param {FileSystemFile} temporaryFile
   * @param {URL} url URL adresa, ze které se byl stažen soubor.
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum]
   * @private
   * @returns {Promise.<Asset>}
   */
  createDownloaded(
    extension,
    extract,
    response,
    temporaryDirectoryPath,
    temporaryFile,
    url,
    { checksum } = {}
  ) {
    return new Promise((resolve) => {
      const lastFetched = new Date();
      if (!response.ok) {
        const error = new RangeError(
          `Invalid HTTP status "${response.status}".`
        );
        error.level = ErrorLevel.Warning;
        throw error;
      }
      const { headers } = response;
      const etag = this.parseEtag(headers);
      const lastModified = this.parseLastModified(headers);
      const targetId = this.generateId(url, {
        etag,
        lastFetched,
        lastModified
      });
      const targetPath = this.parseTargetPath(extension, extract, targetId);
      return resolve(
        this.createTarget(
          extract,
          targetPath,
          temporaryDirectoryPath,
          temporaryFile
        ).then((targetEntry) =>
          this.createAsset(targetId, targetEntry.getPath(), {
            checksum,
            etag,
            lastFetched,
            lastModified,
            url
          })
        )
      );
    });
  }

  /**
   * Rozbalí stažený archiv a vytvoří z něj cílovou složku.
   * Pokud zde již cílová složka je, tak se archiv smaže.
   * @param {FileSystemPath} targetPath
   * @param {FileSystemPath} temporaryDirectoryPath
   * @param {FileSystemFile} temporaryFile
   * @private
   * @returns {Promise.<FileSystemDirectory>}
   */
  createArchive(targetPath, temporaryDirectoryPath, temporaryFile) {
    return new Promise((resolve) =>
      resolve(
        this.fileSystemManager.getDirectory(targetPath).then(
          (targetDirectory) =>
            temporaryFile.remove().then(() => targetDirectory),
          (error) => {
            if (error.name === ErrorName.NotFoundError) {
              return this.fileSystemManager
                .createDirectory(temporaryDirectoryPath)
                .then((temporaryDirectory) =>
                  this.extractArchive(
                    targetPath,
                    temporaryDirectory,
                    temporaryFile
                  ).catch((extractError) =>
                    temporaryDirectory.remove({ recursive: true }).then(() => {
                      throw extractError;
                    })
                  )
                );
            }
            throw error;
          }
        )
      )
    );
  }

  /**
   * Vytvoří metadata záznamu a uloží je do perzistentního úložiště.
   * @param {DsId} id The primary key of the item to generate.
   * @param {FileSystemPath} path Path to the item's entry in the file system.
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum]
   * @param {string} [opts.etag]
   * @param {Date} [opts.lastFetched]
   * @param {Date} [opts.lastModified]
   * @param {URL} [opts.url]
   * @private
   * @returns {Promise.<Asset>}
   */
  createAsset(
    id,
    path,
    { checksum, etag, lastFetched, lastModified, url } = {}
  ) {
    return new Promise((resolve) =>
      resolve(
        this.store.create(
          'asset',
          {
            checksum,
            etag: etag ?? undefined,
            lastFetched: lastFetched?.toISOString(),
            id,
            lastModified: lastModified?.toISOString(),
            path: path.getValue(),
            url: url?.toString()
          },
          {
            // Přepsat celý existující záznam.
            onConflict: 'replace',
            // Pokud již záznam existuje, aktualizuje se.
            upsert: true
          }
        )
      )
    );
  }

  /**
   * Přesune stažený soubor na cílový soubor.
   * Pokud zde již cílový soubor je, tak se stažený soubor smaže.
   * @param {FileSystemPath} targetPath
   * @param {FileSystemFile} temporaryFile
   * @private
   * @returns {Promise.<FileSystemFile>}
   */
  createFile(targetPath, temporaryFile) {
    return new Promise((resolve) =>
      resolve(
        this.fileSystemManager.getFile(targetPath).then(
          (targetFile) => temporaryFile.remove().then(() => targetFile),
          (error) => {
            if (error.name === ErrorName.NotFoundError) {
              return temporaryFile.move(targetPath);
            }
            throw error;
          }
        )
      )
    );
  }

  /**
   * Vytvoří cílový záznam ze staženého souboru.
   * Pokud zde již cílový záznam je, tak se stažený soubor smaže.
   * Bohužel nejde před začátkem stahování detekovat, že byl soubor již stažený.
   * To lze zjistit až z odpovědi na stažení souboru (ETag, Last-Modified).
   * Stejný hash v ID by měl zaručit, že je to stejný soubor.
   * Smazání aktuálního souboru je nebezpečné (může být používaný),
   * přesun a rozbalování je zbytečné.
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {FileSystemPath} targetPath
   * @param {FileSystemPath} temporaryDirectoryPath
   * @param {FileSystemFile} temporaryFile
   * @private
   * @returns {Promise.<FileSystemEntry>}
   */
  createTarget(extract, targetPath, temporaryDirectoryPath, temporaryFile) {
    return new Promise((resolve) => {
      if (extract) {
        return resolve(
          this.createArchive(targetPath, temporaryDirectoryPath, temporaryFile)
        );
      }
      // Soubory se do cílového umístění přesunují.
      return resolve(this.createFile(targetPath, temporaryFile));
    });
  }

  /**
   * Smaže cachovaný záznam a jeho metadata.
   * @param {DsId} id Identifikátor záznamu, který se má smazat.
   * @returns {Promise.<undefined>}
   */
  destroy(id) {
    return new Promise((resolve) => {
      const { store } = this;
      const asset = store.get('asset', id);
      if (isNil(asset)) {
        return resolve();
      }
      const path = this.fileSystemPathFactory.fromValue(asset.path);
      return resolve(
        this.fileSystemManager
          .getEntry(path)
          // Použije se smazání s recursive, protože záznamem může být i neprázdná složka.
          .then((entry) => entry.remove({ recursive: true }))
          .then(() => store.destroy('asset', id))
          .then(noop)
      );
    });
  }

  /**
   * Stáhne nový záznam ze zadané URL.
   * Podporuje cachování, aby se soubory zbytečně opakovaně nestahovaly.
   *
   * Zdroje:
   * - [RequestCache WHATWG](https://fetch.spec.whatwg.org/#concept-request-cache-mode)
   * - [RequestCache MDN](https://developer.mozilla.org/en-US/docs/Web/API/Request/cache)
   * @param {string} url URL adresa, ze které se má stáhnout soubor.
   * @param {Object} [opts={}]
   * @param {boolean} [opts.cache] Příznak, jestli se má soubor cachovat.
   * Hodnota `true` odpovídá hodnotě `force-cache` z `RequestCache`.
   * Soubor se tedy stáhne a pak už se nemusí ověřovat jeho změny na serveru.
   * Hodnota `false` odpovídá hodnotě `no-cache` z `RequestCache`.
   * Při každém požadavku na stažení se na serveru ověří případné změny.
   * @param {string} [opts.checksum] Kontrolní součet, který by měl mít
   * stažený soubor.
   * Používá se pouze pro vyhledání záznamů v souborovém systému,
   * pomocí starého algoritmu.
   * @param {boolean} [opts.extract] Příznak, jestli se má stažený soubor
   * rozbalit do složky.
   * @returns {Promise.<Asset>}
   */
  download(url, { cache = true, checksum, extract = false } = {}) {
    // Nejprve se určí, jaký identifikátor by měl soubor média.
    return new Promise((resolve) => {
      // Převod URL na objekt, což provede normalizaci URL a ulehčí přístup
      // k jejím částem.
      const urlObject = new URL(url);
      // Vyhledá se záznam, který byl stažený z požadované URL.
      // Primárně se hledá dle URL a pokud to nevyjde, URL se zahashuje jako
      // ve starém algoritmu a tím se získá ID, podle kterého se podruhé hledá.
      const asset = this.findAsset(urlObject, { checksum });
      if (isNil(asset)) {
        return resolve(this.create(extract, urlObject, { checksum }));
      }
      // Pokud již soubor existuje a je zapnutá cache, tak se použije.
      if (cache) {
        return resolve(asset);
      }
      // Pokud je cache vypnutá, ověří se aktuálnost na serveru
      // a případně se stáhne nový soubor.
      return resolve(this.update(asset, extract, urlObject));
    });
  }

  /**
   * Rozbalí archiv do složky a smaže ho.
   * @param {FileSystemPath} targetPath
   * @param {FileSystemDirectory} temporaryDirectory
   * @param {FileSystemFile} temporaryFile
   * @private
   * @returns {Promise.<FileSystemDirectory>}
   */
  extractArchive(targetPath, temporaryDirectory, temporaryFile) {
    return new Promise((resolve) =>
      resolve(
        this.fileArchiveManager
          .getVolume(temporaryFile.getPath())
          .then((volume) => volume.extract(temporaryDirectory.getPath()))
          .then(() => temporaryFile.remove())
          .then(() => temporaryDirectory.move(targetPath))
      )
    );
  }

  /**
   * TODO #471 Doc
   * @param {URL} url URL adresa, pro kterou se má najít záznam.
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum] Kontrolní součet, pod kterým by mohl být
   * záznam uložen.
   * @returns {Asset|undefined}
   */
  findAsset(url, { checksum } = {}) {
    if (isNil(checksum)) {
      return this.findLatestAsset(url) ?? this.findLegacyAsset(url);
    }
    return (
      this.findAssetByChecksum(checksum) ??
      this.findLegacyAsset(url, { checksum })
    );
  }

  /**
   * TODO #471 Doc
   * @param {string} checksum Kontrolní součet, pod kterým je
   * záznam uložen.
   * @private
   * @returns {Asset|undefined}
   */
  findAssetByChecksum(checksum) {
    if (isNil(checksum)) {
      return;
    }
    return this.store
      .filter('asset')
      .find((asset) => asset.checksum === checksum);
  }

  /**
   * Vyhledá nejnovější záznam, který byl stažený ze zadané URL.
   * @param {URL} url URL adresa, pro kterou se má najít záznam.
   * @private
   * @returns {Asset|undefined}
   */
  findLatestAsset(url) {
    const urlString = url.toString();
    const matchingAssets = this.store
      .filter('asset')
      .filter((asset) => asset.url === urlString);
    if (!matchingAssets.length) {
      return;
    }
    // TODO Porovnávat i dle identifikátoru:
    // - V případě, že jsou oba datumy NaN, nebo mají shodnou hodnotu.
    // - Vzhledem s sériovému stahování, je ale skoro nemožné,
    //   aby byl fetch odlišný, obzvláště u dvou verzí jednoho média.
    return matchingAssets.reduce((acc, asset) => {
      // Datum stažení assetu, přes který se iteruje.
      // Operátor ?? zajišťuje NaN datum i pro vstup null.
      const lastFetched = new Date(asset.lastFetched ?? undefined);
      // Asset musí mít vyplněné datum stažení, jinak se pooužije předchozí.
      if (isNaN(lastFetched)) {
        return acc;
      }
      const accLastFetched = new Date(acc.lastFetched ?? undefined);
      // Pokud akumulátor nemá datum, nebo je starší než iterovaný,
      // tak se použije iterovaný.
      if (isNaN(accLastFetched) || lastFetched > accLastFetched) {
        return asset;
      }
      // Iterovaný není novější než akumulátor, použije se akumulátor.
      return acc;
    });
  }

  /**
   * Vyhledá záznam stažený ze zadané URL podle starého algoritmu.
   * Slouží k vyhledání souborů uložených v předchozí verzi algoritmu.
   * V této verzi každé URL odpovídal právě jeden cílový soubor, který měl ID
   * dle parametru checksum, případně vytvořené zahashováním URL.
   * @param {URL} url URL adresa, pro kterou se má najít záznam.
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum] Kontrolní součet, pod kterým by mohl být
   * záznam uložen.
   * @private
   * @returns {Asset}
   */
  findLegacyAsset(url, { checksum } = {}) {
    // Určení identifikátoru nového souboru.
    const id = this.generateId(url, { checksum });
    // Vyhledání souboru, který patří k médiu.
    return this.store.get('asset', id);
  }

  /**
   * Vygeneruje identifikátor záznamu.
   *
   * Identifikátor se vždy generuje pouze z jedné dodatečné informace k URL.
   * To je z důvodu, když by se změnila např. hodnota Last-Modified,
   * ale byl by zachován stejný ETag, tak není změna ID očekávaná.
   * Generuje se podle následujících priorit:
   *
   * 1. `checksum` ze staré verze algoritmu se přebírá jako ID.
   * 2. `etag` je primární zdroj informace o změně média.
   * 3. `lastModified` se použije, pokud server neposlal ETag.
   * 4. `lastFetched` se použije, pokud server neposlal ani Last-Modified.
   * 5. `url` se použije, pokud není dostupné nic jiného, stejně jako
   *    ve staré verzi algoritmu.
   *
   * Identifikátor se generuje proto, aby se odstranily problémy
   * se speciálními znaky v názvu souboru a délkou jeho názvu.
   * Zároveň to umožní k jedné URL ukládat více verzí souborů,
   * které z ní byly stažené.
   * @param {URL} url URL adresa, ze které se má stáhnout soubor,
   * pro který se generuje identifikátor.
   * @param {Objet} [opts={}]
   * @param {string} [opts.checksum] Kontrolní součet, pod kterým by mohl být
   * záznam uložen podle starého algoritmu.
   * @param {string} [opts.etag] ETag záznamu.
   * @param {number} [opts.index] Pořadí vygenerovaného identifikátoru,
   * které umožňuje k jedné sadě informací identifikující záznam vytvořit
   * neomezené množství identifikátoru např. pro uložení více dočasných souborů.
   * @param {Date} [opts.lastFetched] Datum posledního ověření platnosti záznamu.
   * @param {Date} [opts.lastModified] Datum poslední změny záznamu na serveru.
   * @private
   * @returns {DsId}
   */
  generateId(
    url,
    { checksum, etag, index = 0, lastFetched, lastModified } = {}
  ) {
    if (!isNil(checksum)) {
      return checksum;
    }
    if (!isNil(etag)) {
      return objectHash({
        etag,
        index,
        url: url.toString()
      });
    }
    if (!isNil(lastModified)) {
      return objectHash({
        index,
        lastModified: lastModified.toISOString(),
        url: url.toString()
      });
    }
    if (!isNil(lastFetched)) {
      return objectHash({
        index,
        lastFetched: lastFetched.toISOString(),
        url: url.toString()
      });
    }
    if (index !== 0) {
      return objectHash({
        index,
        url: url.toString()
      });
    }
    // Výchozím zdrojem identifikátoru je URL, na které je uložený binární obsah.
    // Na URL se ještě provede hash.
    // To odstraní speciální znaky o omezí délku pojmenování souboru.
    return objectHash(url.toString());
  }

  /**
   * Lists all files with detailed information for a requested resource.
   * @private
   * @returns {Promise.<Array.<AssetDownloader~FileInfo>>} List of infos about files.
   */
  listFile() {
    return new Promise((resolve) => {
      const { basePath } = this;
      return resolve(
        this.fileSystemManager.getDirectory(basePath).then(
          (directory) =>
            directory
              .list()
              .then((entryArray) =>
                entryArray.map((entry) => this.parseFile(entry))
              ),
          (error) => {
            // Pokud složka s workspace neexistuje, považuje se za prázdnou.
            if (error.name === ErrorName.NotFoundError) {
              return [];
            }
            throw error;
          }
        )
      );
    });
  }

  /**
   * Načte volitelné datum.
   * @param {string} [string]
   * @private
   * @returns {Date|undefined}
   */
  parseDate(string) {
    if (isNil(string)) {
      return;
    }
    const date = new Date(string);
    if (isNaN(date.getTime())) {
      return;
    }
    return date;
  }

  /**
   * Načte hodnotu ETagu z HTTP hlaviček.
   * @param {Headers} headers
   * @private
   * @returns {string|undefined}
   */
  parseEtag(headers) {
    return headers.get('ETag') ?? undefined;
  }

  /**
   * Načte příponu souboru z URL, ze které se stahuje.
   * Algoritmus se snaží příponu ve staženém souboru zachovat.
   *
   * URL s příponou, která je shodná s dočasnou, se zablokuje.
   * To je problém, protože se nejprve vytvoří `${id}.tmp.tmp`, což nevadí,
   * ale po stažení se přesune na `${id}.tmp`, což je cesta, která označuje
   * dočasný soubor, které jsou při každém spuštění zařízení smazány.
   * @param {URL} url URL adresa, ze které se má zjistit přípona.
   * @private
   * @returns {string}
   */
  parseExtension(url) {
    const extension = this.pathBrowserify.extname(url.pathname).slice(1);
    if (extension === this.temporaryExtension) {
      throw new TypeError(`Invalid URL extension "${extension}".`);
    }
    return extension;
  }

  /**
   * Načte datum Last-Modified z HTTP hlaviček.
   * @param {Headers} headers
   * @private
   * @returns {Date|undefined}
   */
  parseLastModified(headers) {
    return this.parseDate(headers.get('Last-Modified'));
  }

  /**
   * Parses an information about a file.
   * @param {FileSystemEntry} entry File's entry in the file system.
   * @private
   * @returns {AssetDownloader~FileInfo} Info about the file.
   */
  parseFile(entry) {
    const path = entry.getPath();
    return {
      entry,
      id: path.getStem(),
      temporary: path.getExtension() === this.temporaryExtension
    };
  }

  /**
   * Načte cílovou cestu, na které by měl být uložený záznam.
   * @param {string} extension
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {DsId} id Identifikátor záznamu, jehož cesta se načítá.
   * @private
   * @returns {FileSystemPath}
   */
  parseTargetPath(extension, extract, id) {
    const { fileSystemPathFactory } = this;
    // Název cílového vytvořeného souboru.
    let targetFilePath;
    if (extract) {
      // Archivy se rozbalují do složky pojmenované dle id.
      targetFilePath = fileSystemPathFactory.fromInfo({ stem: id });
    } else {
      // Soubory se pojmenovávají dle id a zachovává se přípona.
      targetFilePath = fileSystemPathFactory.fromInfo({ extension, stem: id });
    }
    // Cesta k vytvořenému souboru.
    return fileSystemPathFactory.join(this.basePath, targetFilePath);
  }

  /**
   * Určí na jakou dočasnou cestu uložit záznam.
   * @param {DsId} id Identifikátor záznamu, pro který se určuje cesta.
   * @private
   * @returns {FileSystemPath}
   */
  parseTemporaryPath(id) {
    const { fileSystemPathFactory } = this;
    const temporaryFilePath = fileSystemPathFactory.fromInfo({
      extension: this.temporaryExtension,
      stem: id
    });
    return fileSystemPathFactory.join(this.basePath, temporaryFilePath);
  }

  /**
   * Určí na jakou dočasnou cestu rozbalit stažený archiv do složky.
   * Z každé URL se rozbaluje do unikátní cesty (mezi ostatními URL),
   * ale zároveň do stále stejné, aby se zablokovali případná paralelní
   * rozbalení archiv ze stejné URL.
   * @param {URL} url URL záznamu, pro který se určuje cesta.
   * @private
   * @returns {FileSystemPath}
   */
  parseTemporaryDirectoryPath(url) {
    // Vygeneruje se identifikátor pro danou URL.
    // Zároveň se předá index, protože jde o druhý dočasný soubor.
    // Tím se vygeneruje identifikátor a cesta odlišná od staženého souboru.
    const temporaryId = this.generateId(url, { index: 1 });
    // Z identifikátoru se vytvoří dočasná cesta.
    return this.parseTemporaryPath(temporaryId);
  }

  /**
   * Určí na jakou dočasnou cestu uložit stahovaný soubor.
   * Z každé URL se stahuje do unikátní cesty (mezi ostatními URL),
   * ale zároveň do stále stejné, aby se zablokovali případná paralelní
   * stahování ze stejné URL.
   * @param {URL} url URL záznamu, pro který se určuje cesta.
   * @private
   * @returns {FileSystemPath}
   */
  parseTemporaryFilePath(url) {
    // Vygeneruje se identifikátor pro danou URL.
    const temporaryId = this.generateId(url);
    // Z identifikátoru se vytvoří dočasná cesta.
    return this.parseTemporaryPath(temporaryId);
  }

  /**
   * Obnoví uložené záznamy a jejich metadata.
   * Synchronizuje obsah souborového systému a metadat.
   * Vytvoří chybějící metadata a smaže metadata bez záznamu.
   * Také se smažou všechny dočasné soubory, např. pro případ vypnutí zařízení
   * během předchozího stahování.
   * @returns {Promise.<Array.<Asset>>}
   */
  restore() {
    return new Promise((resolve) => {
      const { promiseUtil } = this;
      return resolve(
        promiseUtil
          .allSerial([
            // Obnoví se informace o uložených souborech z LocalStorage.
            () => this.store.findAll('asset'),
            // Obnoví se informace o uložených souborech ze souborového systému.
            () => this.listFile()
          ])
          .then(([assets, files]) => {
            // Informace se rozdělí na skupiny dle ID.
            const assetsById = groupBy(assets, 'id');
            const filesById = groupBy(files, 'id');
            // Z klíčů skupin se určí všechny ID, které byly obnovené.
            const ids = uniq([
              ...Object.keys(assetsById),
              ...Object.keys(filesById)
            ]);
            // Informace ze skupin se pro každé ID slouči.
            // Metadata jsou uložená ve Store, takže zde je vždy maximálně jedna
            // položka pro jedno ID.
            return ids.map((id) => ({
              asset: assetsById[id]?.[0],
              id,
              files: filesById[id]
            }));
          })
          // Jednotlivé skupiny informací se postupně obnoví.
          // Selhání obnovení jedné skupiny nepřeruší obnovení ostatních.
          .then((groups) =>
            promiseUtil.allSerial(
              groups.map(
                (group) => () =>
                  this.restoreGroup(group.id, {
                    asset: group.asset,
                    files: group.files
                  }).catch((error) => {
                    this.logger.error(error);
                  })
              )
            )
          )
          // Vrátí se pouze ta metadata, která nebyla při obnovení smazána.
          .then((assets) => assets.filter((asset) => !isNil(asset)))
      );
    });
  }

  /**
   * Obnoví skupinu uložených záznamy a jejich metadat se stejným ID.
   * @param {DsId} id
   * @param {Object} [opts={}]
   * @param {Asset} [opts.asset]
   * @param {Array.<AssetDownloader~FileInfo>} [opts.files]
   * @private
   * @returns {Promise.<Asset|undefined>}
   */
  restoreGroup(id, { asset, files = [] } = {}) {
    return new Promise((resolve) => {
      const { promiseUtil } = this;
      // Záznamy se rozdělí na dočasné a cílové.
      const filesByTemporary = groupBy(files, 'temporary');
      const targetFiles = filesByTemporary.false ?? [];
      // Každá skupina by měla mít právě jeden cílový soubor.
      if (targetFiles.length === 1) {
        const targetFile = targetFiles[0];
        const temporaryFiles = filesByTemporary.true ?? [];
        return resolve(
          // Smažou se pouze všechny dočasné záznamy z dané skupiny.
          // Selhání mazání jednoho záznamu nepřeruší mazání ostatních.
          promiseUtil
            .allSerial(
              temporaryFiles.map(
                (file) => () =>
                  // Použije se smazání s recursive, protože záznamem může být
                  // i neprázdná složka.
                  file.entry.remove({ recursive: true }).catch((error) => {
                    this.logger.error(error);
                  })
              )
            )
            // Pokud k této validní skupině chybí metadata, vytvoří se.
            // Tento přístup dokáže obnovit pouze základní informace (id, cesta).
            // Metadata se vytváří, aby pomocí nich mohl být k souboru přístup
            // a mohl tak být využitý, případně smazaný v rámci čištění obsahu.
            .then(() => {
              if (isNil(asset)) {
                return this.createAsset(id, targetFile.entry.getPath());
              }
              return asset;
            })
        );
      }
      // Cílový záznam buď chybí, nebo je jich zde moc a není možné rozhodnout,
      // který k danému ID patří.
      return resolve(
        // Smažou se všechny záznamy z dané skupiny.
        // Selhání mazání jednoho souboru nepřeruší mazání ostatních.
        promiseUtil
          .allSerial(
            files.map(
              (file) => () =>
                // Použije se smazání s recursive, protože záznamem může být
                // i neprázdná složka.
                file.entry.remove({ recursive: true }).catch((error) => {
                  this.logger.error(error);
                })
            )
          )
          .then(() => {
            if (!isNil(asset)) {
              return this.store.destroy('asset', id).then(noop);
            }
          })
      );
    });
  }

  /**
   * Aktualizuje záznam, který je již přítomný v cache.
   * Chová se jako `force-cache` z `RequestCache`.
   * Opakuje požadavek na stažení s využitím HTTP hlaviček `If-None-Match`
   * a `If-Modified-Since`, aby ke stažení došlo pouze při změně souboru
   * na serveru.
   * @param {Asset} asset
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {URL} url URL adresa, ze které se má stáhnout soubor.
   * @private
   * @returns {Promise.<Asset>}
   */
  update(asset, extract, url) {
    return new Promise((resolve) => {
      // Načtou se potřebné hodnoty z aktuálního záznamu.
      // Pozor, nesmí se nikdy míchat hodnoty ze starého a nového záznamu.
      // Pokud se vrátí 304, nebo jiný neúspěšný status, zachovat staré hodnoty.
      // V opačném případě použít vždy nové hlavičky.
      const { etag, id } = asset;
      const lastModified = this.parseDate(asset.lastModified);
      // Cesta k vytvořenému souboru.
      const currentPath = this.fileSystemPathFactory.fromValue(asset.path);
      const temporaryDirectoryPath = this.parseTemporaryDirectoryPath(url);
      const temporaryFilePath = this.parseTemporaryFilePath(url);
      // Zjištění názvu souboru, validace a parsování jeho přípony.
      // Určuje se předem, aby se zbytečně nestahovalo,
      // pokud by přípona nebyla validní.
      const extension = this.parseExtension(url);
      return resolve(
        this.fileTransferManager
          .download(temporaryFilePath, url.toString(), {
            headers: omitBy(
              {
                'If-Modified-Since': lastModified?.toUTCString(),
                'If-None-Match': etag
              },
              (value) => isNil(value)
            )
          })
          .then(
            ({ file, response }) =>
              this.updateDownloaded(
                currentPath,
                extension,
                extract,
                id,
                response,
                temporaryDirectoryPath,
                file,
                url,
                { etag, lastModified }
              )
                // Ošetření selhání v operacích po stažení souboru.
                .catch((error) => {
                  // Pokud soubor nebyl stažen, nic se nedělá.
                  if (isNil(file)) {
                    throw error;
                  }
                  // Pokud byl soubor stažen, smaže se.
                  return file.remove().then(() => {
                    throw error;
                  });
                }),
            (error) => {
              if (error.name === ErrorName.NetworkError) {
                error.level = ErrorLevel.Warning;
              }
              throw error;
            }
          )
      );
    });
  }

  /**
   * Dokončí aktualizaci záznamu po jeho stažení.
   *
   * Po stažení nové verze záznamu se stará verze nemaže,
   * protože se může přehrávat v aktivním obsahu.
   * Také je zde šance, že se obsah záznamu vrátí zpět na již dříve stažený
   * soubor a bude se tak využívat znovu.
   * Smazání neaktuálních verzí záznamu se provádí v případě potřeby
   * v rámci čištění místa při jeho zaplnění.
   * @param {FileSystemPath} currentPath
   * @param {string} extension
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {DsId} id
   * @param {Response} response
   * @param {FileSystemPath} temporaryDirectoryPath
   * @param {FileSystemFile} temporaryFile
   * @param {URL} url URL adresa, ze které byl stažen soubor.
   * @param {Object} [opts={}]
   * @param {string} [opts.etag]
   * @param {Date} [opts.lastModified]
   * @private
   * @returns {Promise.<Asset>}
   */
  updateDownloaded(
    currentPath,
    extension,
    extract,
    id,
    response,
    temporaryDirectoryPath,
    temporaryFile,
    url,
    { etag, lastModified } = {}
  ) {
    return new Promise((resolve) => {
      const lastFetched = new Date();
      const { status } = response;
      // Pokud přišel status 304 Not Modified, není nutné soubor stahovat.
      // Zachová se původní ETag a Last-Modified.
      // Pouze se aktualizuje datum stažení, aby odpovídalo okamžiku,
      // kdy je jisté, že tato verze média je na serveru platná.
      if (status === 304) {
        return resolve(
          this.createAsset(id, currentPath, {
            etag,
            lastFetched,
            lastModified,
            url
          })
        );
      }
      if (!response.ok) {
        const error = new RangeError(`Invalid HTTP status "${status}".`);
        error.level = ErrorLevel.Warning;
        throw error;
      }
      const { headers } = response;
      const newEtag = this.parseEtag(headers);
      const newLastModified = this.parseLastModified(headers);
      const newId = this.generateId(url, {
        etag: newEtag,
        lastFetched,
        lastModified: newLastModified
      });
      const targetPath = this.parseTargetPath(extension, extract, newId);
      return resolve(
        this.createTarget(
          extract,
          targetPath,
          temporaryDirectoryPath,
          temporaryFile
        ).then((targetEntry) =>
          this.createAsset(newId, targetEntry.getPath(), {
            etag: newEtag,
            lastFetched,
            lastModified: newLastModified,
            url
          })
        )
      );
    });
  }
}

/**
 * Informace o záznamu ve složce s cachovanými soubory
 * @typedef {Object} AssetDownloader~FileInfo
 * @property {FileSystemEntry} entry Záznam se souborem v souborovém systému
 * @property {string} id Id of a model which is associated with a file
 * @property {boolean} temporary True if file is temporary an will be moved
 * to the final destination.
 */

export default AssetDownloader;
